import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useRunEmailExecTemplateMutation } from '../../../../../queries/EmailExecAssistant/useRunEmailExecTemplateMutation';
import { useGetSystemJobQuery } from '../../../../../queries/SystemJob/useGetSystemJobQuery';
import {
	updateEmailExecAssistantTemplatesRQCache,
	useEmailExecAssistantTemplatesSuspenseQuery,
} from '../../../../../queries/EmailExecAssistant/useEmailExecTemplates';
import {
	invalidateLatestEmailExecDigest,
	useLatestEmailExecDigestQuery,
} from '../../../../../queries/EmailExecAssistant/useLatestEmailExecDigestQuery';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
	ComboboxValue,
} from '../../../../components-v2/Combobox';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { MAX_IGNORE_NOISE_NOTES_LENGTH, MAX_KEYWORDS } from './constants';
import {
	canScheduleEmailExec,
	DAY_OF_WEEK_NAMES,
	DEFAULT_SCHEDULE_DAY_INDEXES,
	getDefaultScheduleMinute,
	getScheduleTimeOptions,
	getScheduleTimezone,
	getTimezoneLabel,
	getUtcOffsetMinutes,
	localScheduleToUtcSlots,
	type ScheduleTimeOption,
	utcSlotsToLocalSchedule,
} from './scheduleUtils';
import { styleSheet } from './styles';
import { addKeywords, splitKeywordInput } from './utils';
import { Field, FieldDescription, FieldError, FieldLabel } from '@HoudiniUI/Field';
import { InputGroupAddon, InputGroupText, InputGroupTextarea, InputGroupV2 } from '@HoudiniUI/InputGroup';
import { Label } from '@HoudiniUI/Label';
import { PlayIcon } from '@WebComponents/svgs/icons/PlayIcon';
import { useCreateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useCreateEmailExecAssistantTemplateMutation';
import { useUpdateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useUpdateEmailExecAssistantTemplateMutation';
import { Input } from '../../../../components-v2/Input';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

const RUN_NOW_FALLBACK_ERROR = "We couldn't finish your brief. Please try again.";

const RUN_JOB_POLL_INTERVAL_MS = 3000;

const isTerminalRunStatus = (status: Api.EmailExecBriefRunStatus | undefined) =>
	status === 'Success' || status === 'Failed';

/** The user-editable subset of the template; everything else is server-managed and round-tripped. */
interface EditableFields {
	name: string;
	objectivePrompt: string;
	monitoredKeywords: string[];
	ignoreNoiseNotes: string;
	scheduleEnabled: boolean;
	scheduleDayIndexes: number[];
	scheduleMinuteOfDay: number;
}

const sameKeywords = (a: string[], b: string[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const sameDayIndexes = (a: number[], b: number[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const seedScheduleFields = (
	template: Api.EmailExecAssistantTemplate | null,
	offsetMinutes: number,
	defaultMinuteOfDay: number
): Pick<EditableFields, 'scheduleEnabled' | 'scheduleDayIndexes' | 'scheduleMinuteOfDay'> => {
	const stored = utcSlotsToLocalSchedule(template?.scheduleSlots, offsetMinutes);
	return {
		scheduleEnabled: !!template?.scheduleEnabled,
		scheduleDayIndexes: stored?.dayIndexes ?? [...DEFAULT_SCHEDULE_DAY_INDEXES],
		scheduleMinuteOfDay: stored?.minuteOfDay ?? defaultMinuteOfDay,
	};
};

const templateToEditableFields = (
	template: Api.EmailExecAssistantTemplate,
	offsetMinutes: number,
	defaultMinuteOfDay: number
): EditableFields => {
	return {
		name: template.name ?? '',
		objectivePrompt: template.objectivePrompt ?? '',
		monitoredKeywords: template.monitoredKeywords ?? [],
		ignoreNoiseNotes: template.ignoreNoiseNotes ?? '',
		...seedScheduleFields(template, offsetMinutes, defaultMinuteOfDay),
	};
};

const initialEditableFields = (name: string, offsetMinutes: number, defaultMinuteOfDay: number): EditableFields => ({
	name: name ? `Exec Assistant for ${name}` : '',
	objectivePrompt: '',
	monitoredKeywords: [],
	ignoreNoiseNotes: '',
	...seedScheduleFields(null, offsetMinutes, defaultMinuteOfDay),
});

/**
 * The UI enforces one template per user; the backend list may (anomalously, via impersonation/API)
 * hold more than one. Bind deterministically to the most-recently-modified and ignore the rest.
 */
const selectBoundTemplate = (
	templates: Api.EmailExecAssistantTemplate[]
): Api.EmailExecAssistantTemplate | undefined => {
	if (templates.length <= 1) {
		return templates[0];
	}
	return [...templates].sort((a, b) => (b.lastModifiedDate ?? '').localeCompare(a.lastModifiedDate ?? ''))[0];
};

export const EmailExecAssistantSettingsContent: React.FC = () => {
	const id = React.useId();
	const userSession = useUserSession();
	const { logEvent, logApiError } = useEventLogging('EmailExecAssistantSettings');

	const timeZone = getScheduleTimezone(userSession.user);
	const offsetMinutes = React.useMemo(() => getUtcOffsetMinutes(timeZone), [timeZone]);
	const timeOptions = React.useMemo(() => getScheduleTimeOptions(offsetMinutes), [offsetMinutes]);
	const defaultScheduleMinute = React.useMemo(() => getDefaultScheduleMinute(timeOptions), [timeOptions]);
	const scheduleEligible = canScheduleEmailExec(userSession.user);

	const { data: templates } = useEmailExecAssistantTemplatesSuspenseQuery({
		meta: { toastErrorMessage: 'Failed to load your Email Exec Assistant settings.' },
		staleTime: Infinity,
	});
	const latestDigestQuery = useLatestEmailExecDigestQuery();
	// The full server template we round-trip on save (PUT is a whole-object replace); null until created.
	const loadedTemplate = selectBoundTemplate(templates) ?? null;
	// `baseline` is the last-saved editable state — the reference for dirty-tracking and Discard.
	const baseline = templates?.length
		? templateToEditableFields(loadedTemplate, offsetMinutes, defaultScheduleMinute)
		: initialEditableFields(getDisplayName(userSession.user), offsetMinutes, defaultScheduleMinute);

	const [form, setForm] = React.useState<EditableFields>(
		templates?.length
			? templateToEditableFields(loadedTemplate, offsetMinutes, defaultScheduleMinute)
			: initialEditableFields(getDisplayName(userSession.user), offsetMinutes, defaultScheduleMinute)
	);
	const [keywordInput, setKeywordInput] = React.useState('');

	// Telemetry carries counts/booleans only — never keyword/prompt content (sensitive inbox data).
	const savedTemplateEventPayload = (template: Api.EmailExecAssistantTemplate, saved: EditableFields) => ({
		isCreate: !loadedTemplate?.id,
		keywordCount: saved.monitoredKeywords.length,
		hasNoiseNotes: saved.ignoreNoiseNotes.length > 0,
		scheduleEnabled: !!template.scheduleEnabled,
		scheduleDayCount: template.scheduleSlots?.length ?? 0,
		scheduleHourLocal: Math.floor(saved.scheduleMinuteOfDay / 60),
		scheduleMinuteLocal: saved.scheduleMinuteOfDay % 60,
	});
	const updateEmailExecAssistantMutation = useUpdateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template, offsetMinutes, defaultScheduleMinute);
			logEvent('EmailExecTemplateUpdated', savedTemplateEventPayload(template, saved));
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateUpdate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});
	const createEmailExecAssistantMutation = useCreateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template, offsetMinutes, defaultScheduleMinute);
			logEvent('EmailExecTemplateCreated', savedTemplateEventPayload(template, saved));
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateCreate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});

	const runBriefMutation = useRunEmailExecTemplateMutation({
		onSuccess: job => {
			// Over the daily cap the endpoint returns 200 with an already-Failed job carrying the reason.
			if (job.status === 'Failed') {
				logEvent('EmailExecRunNow-Error', { rejected: true, hasServerMessage: !!job.errorMessage });
				toast.error(job.errorMessage || RUN_NOW_FALLBACK_ERROR);
			}
		},
		onError: error => {
			logEvent('EmailExecRunNow-Error');
			const friendlyMessage = error.systemCode === 400 ? error.systemMessage : null;
			toast.error(friendlyMessage || "We couldn't run your brief. Please try again.");
		},
	});
	const startedJob = runBriefMutation.data;
	// A cap-rejected start arrives already Failed — never treat it as the active run.
	const activeRunJobId = startedJob && !isTerminalRunStatus(startedJob.status) ? startedJob.id : undefined;
	const resetRunBrief = runBriefMutation.reset;
	const runJobQuery = useGetSystemJobQuery<Api.IEmailExecBriefSystemJob>({
		enabled: !!activeRunJobId,
		jobId: activeRunJobId ?? '',
		refetchInterval: query => (isTerminalRunStatus(query.state.data?.status) ? false : RUN_JOB_POLL_INTERVAL_MS),
	});
	const runJob = runJobQuery.data;

	React.useEffect(() => {
		if (!activeRunJobId || runJob?.id !== activeRunJobId || !isTerminalRunStatus(runJob.status)) {
			return;
		}
		resetRunBrief();
		if (runJob.status === 'Success') {
			invalidateLatestEmailExecDigest();
			toast.success('Your brief is on its way to your inbox.');
		} else {
			logEvent('EmailExecRunNow-Error', { hasServerMessage: !!runJob.errorMessage });
			toast.error(runJob.errorMessage || RUN_NOW_FALLBACK_ERROR);
		}
	}, [activeRunJobId, runJob, resetRunBrief, logEvent]);

	const isGenerating = runBriefMutation.isPending || !!activeRunJobId;

	const isDirty =
		form.name !== baseline.name ||
		form.objectivePrompt !== baseline.objectivePrompt ||
		form.ignoreNoiseNotes !== baseline.ignoreNoiseNotes ||
		!sameKeywords(form.monitoredKeywords, baseline.monitoredKeywords) ||
		form.scheduleEnabled !== baseline.scheduleEnabled ||
		form.scheduleMinuteOfDay !== baseline.scheduleMinuteOfDay ||
		!sameDayIndexes(form.scheduleDayIndexes, baseline.scheduleDayIndexes);
	const isSaving = createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending;
	const scheduleValid = !form.scheduleEnabled || form.scheduleDayIndexes.length > 0;
	const canSave =
		isDirty &&
		scheduleValid &&
		form.objectivePrompt.trim().length > 0 &&
		form.name.trim().length > 0 &&
		!isSaving &&
		!isGenerating;
	const canRunNow = !!loadedTemplate?.id && !isDirty && !isGenerating && !isSaving;

	const updateField = <TKey extends keyof EditableFields>(key: TKey, value: EditableFields[TKey]) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};

	const onToggleScheduleDay = (dayIndex: number, checked: boolean) => {
		setForm(prev => ({
			...prev,
			scheduleDayIndexes: checked
				? [...new Set([...prev.scheduleDayIndexes, dayIndex])].sort((a, b) => a - b)
				: prev.scheduleDayIndexes.filter(day => day !== dayIndex),
		}));
	};

	const scheduleFieldsForSave = (): Partial<
		Pick<Api.EmailExecAssistantTemplate, 'scheduleEnabled' | 'scheduleSlots'>
	> => {
		if (!scheduleEligible) {
			return {};
		}
		if (!form.scheduleEnabled && !loadedTemplate?.scheduleSlots?.length) {
			return { scheduleEnabled: false };
		}
		return {
			scheduleEnabled: form.scheduleEnabled,
			scheduleSlots: localScheduleToUtcSlots(
				{ dayIndexes: form.scheduleDayIndexes, minuteOfDay: form.scheduleMinuteOfDay },
				offsetMinutes
			),
		};
	};

	const onKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Enter' && event.key !== ',') {
			return;
		}
		// Straight-add: commit the typed text as chip(s) on Enter/comma — no dropdown item, no dialog.
		event.preventDefault();
		const additions = splitKeywordInput(keywordInput);
		if (additions.length === 0) {
			return;
		}
		setForm(prev => ({ ...prev, monitoredKeywords: addKeywords(prev.monitoredKeywords, additions) }));
		setKeywordInput('');
	};

	const onSave = () => {
		if (!canSave) {
			return;
		}
		if (loadedTemplate?.id) {
			updateEmailExecAssistantMutation.mutate({
				...loadedTemplate,
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
				...scheduleFieldsForSave(),
			});
		} else {
			createEmailExecAssistantMutation.mutate({
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
				isActive: true,
				...scheduleFieldsForSave(),
			});
		}
	};

	const onDiscard = () => {
		setForm(baseline);
		setKeywordInput('');
	};

	const onRunNow = () => {
		if (!canRunNow || !loadedTemplate?.id) {
			return;
		}
		logEvent('EmailExecRunNowClicked');
		runBriefMutation.mutate(loadedTemplate.id);
	};

	const selectedTimeOption = timeOptions.find(option => option.minuteOfDay === form.scheduleMinuteOfDay) ?? null;
	const timezoneLabel = getTimezoneLabel(timeZone);
	const lastBriefDate = latestDigestQuery.data?.runDate;

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.runNowContainer)}>
				<Button onClick={onRunNow} isLoading={isGenerating} disabled={!canRunNow}>
					<PlayIcon /> Run now
				</Button>
				{isGenerating ? (
					<span className={css(styleSheet.helpText)}>
						{runJob?.status === 'InProgress' ? 'Generating your brief…' : 'Queued…'}
					</span>
				) : !loadedTemplate?.id ? (
					<span className={css(styleSheet.helpText)}>Save your settings to enable Run now.</span>
				) : isDirty ? (
					<span className={css(styleSheet.helpText)}>Save your changes to run a brief with them.</span>
				) : null}
			</div>
			<Field>
				<FieldLabel htmlFor={`${id}-name`}>
					Assistant name <span className={css(styleSheet.requiredIndicator)}>*</span>
				</FieldLabel>
				<Input
					id={`${id}-name`}
					value={form.name}
					onChange={event => updateField('name', event.target.value)}
					required
				/>
			</Field>
			<Field>
				<FieldLabel htmlFor='email-exec-objective'>
					What should your assistant prioritize? <span className={css(styleSheet.requiredIndicator)}>*</span>
				</FieldLabel>
				<InputGroupV2>
					<InputGroupTextarea
						id='email-exec-objective'
						placeholder='Describe what matters in your inbox — the clients, topics, or situations you want watched and flagged. e.g. “Surface anything about tax-loss harvesting, Roth conversions, or money movement for my top clients.”'
						value={form.objectivePrompt}
						onChange={event => updateField('objectivePrompt', event.target.value)}
						required
						styles={[styleSheet.textArea]}
					/>
					<InputGroupAddon align='blockEnd'>
						<InputGroupText styles={[styleSheet.helpText]}>
							{form.objectivePrompt.trim().length > 0
								? 'Describe the clients, topics, or situations you want watched and flagged.'
								: 'Tell the assistant what to prioritize before saving.'}
						</InputGroupText>
					</InputGroupAddon>
				</InputGroupV2>
			</Field>

			<Field>
				<FieldLabel htmlFor={`${id}-keywords`}>Always-flag keywords</FieldLabel>
				<Combobox
					multiple
					value={form.monitoredKeywords}
					onValueChange={value => updateField('monitoredKeywords', value as string[])}
					inputValue={keywordInput}
					onInputValueChange={setKeywordInput}
				>
					<ComboboxChips>
						<ComboboxValue>
							{(keywords: string[]) => (
								<>
									{keywords.map(keyword => (
										<ComboboxChip key={keyword} aria-label={keyword}>
											{keyword}
											<ComboboxChipRemove />
										</ComboboxChip>
									))}
									<ComboboxChipsInput
										id={`${id}-keywords`}
										placeholder={keywords.length > 0 ? '' : 'Type a keyword and press Enter'}
										onKeyDown={onKeywordKeyDown}
									/>
								</>
							)}
						</ComboboxValue>
					</ComboboxChips>
				</Combobox>
				<FieldDescription>
					Emails containing any of these are always surfaced as action-needed, even if nothing else stands out.
				</FieldDescription>
				<FieldDescription>
					{form.monitoredKeywords.length}/{MAX_KEYWORDS}
				</FieldDescription>
			</Field>

			<Field>
				<FieldLabel htmlFor='email-exec-noise'>What to ignore</FieldLabel>
				<InputGroupV2>
					<InputGroupTextarea
						id='email-exec-noise'
						placeholder='Describe the noise you don’t want in your brief. e.g. “Ignore wholesaler pitches, fixed-income solicitations, and conference invitations.”'
						value={form.ignoreNoiseNotes}
						onChange={event =>
							updateField('ignoreNoiseNotes', event.target.value.slice(0, MAX_IGNORE_NOISE_NOTES_LENGTH))
						}
						styles={[styleSheet.textArea]}
					/>
					<InputGroupAddon align='blockEnd'>
						<InputGroupText styles={[styleSheet.helpText]}>
							{form.ignoreNoiseNotes.length}/{MAX_IGNORE_NOISE_NOTES_LENGTH}
						</InputGroupText>
					</InputGroupAddon>
				</InputGroupV2>
			</Field>

			<Field>
				<Field orientation='horizontal'>
					<Checkbox
						id={`${id}-schedule-enabled`}
						checked={form.scheduleEnabled}
						disabled={!scheduleEligible}
						onCheckedChange={checked => updateField('scheduleEnabled', checked === true)}
					/>
					<FieldLabel htmlFor={`${id}-schedule-enabled`}>Email me my brief on a schedule</FieldLabel>
				</Field>
				{!scheduleEligible ? (
					<FieldDescription>
						Scheduled briefs require a Gmail, Microsoft 365, Yahoo, or AOL email connection.
					</FieldDescription>
				) : form.scheduleEnabled ? (
					<div className={css(styleSheet.scheduleFields)}>
						<div className={css(styleSheet.scheduleDaysRow)}>
							Send on
							{DAY_OF_WEEK_NAMES.map((dayName, dayIndex) => (
								<Label key={dayName} styles={[styleSheet.scheduleDay]} htmlFor={`${id}-schedule-day-${dayIndex}`}>
									<Checkbox
										id={`${id}-schedule-day-${dayIndex}`}
										checked={form.scheduleDayIndexes.includes(dayIndex)}
										onCheckedChange={checked => onToggleScheduleDay(dayIndex, checked === true)}
									/>
									{dayName.slice(0, 3)}
								</Label>
							))}
							at
							<div className={css(styleSheet.scheduleTimeRow)}>
								<Combobox
									items={timeOptions}
									value={selectedTimeOption}
									onValueChange={item => {
										const option = item as ScheduleTimeOption | null;
										if (option) {
											updateField('scheduleMinuteOfDay', option.minuteOfDay);
										}
									}}
									itemToStringLabel={item => (item as ScheduleTimeOption).label}
									isItemEqualToValue={(a, b) =>
										(a as ScheduleTimeOption).minuteOfDay === (b as ScheduleTimeOption).minuteOfDay
									}
								>
									<ComboboxInput aria-label='Delivery time' styles={[styleSheet.timeComboboxInput]} />
									<ComboboxContent>
										<ComboboxEmpty>No matching times</ComboboxEmpty>
										<ComboboxList>
											{(item: ScheduleTimeOption) => (
												<ComboboxItem key={item.minuteOfDay} value={item}>
													<ComboboxItemIndicator />
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
								<span className={css(styleSheet.helpText)}>{timezoneLabel}</span>
							</div>
						</div>
						{form.scheduleDayIndexes.length === 0 ? <FieldError>Choose at least one day.</FieldError> : null}
					</div>
				) : null}
				{lastBriefDate ? (
					<span className={css(styleSheet.statusLine)}>
						Last brief sent {moment(lastBriefDate).tz(timeZone).format('MMM D, YYYY [at] h:mm A')}
					</span>
				) : null}
			</Field>

			<div className={css(styleSheet.footer)}>
				<Button
					onClick={onSave}
					isLoading={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					disabled={!canSave}
				>
					Save
				</Button>
				{isDirty ? (
					<Button
						variant='secondary'
						onClick={onDiscard}
						disabled={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					>
						Discard changes
					</Button>
				) : null}
			</div>
		</div>
	);
};

export const EmailExecAssistantSettings: React.FC = () => {
	const userSession = useUserSession();

	// MUST stay outside Suspense: useSuspenseQuery can't be disabled, so we gate by not rendering Content.
	if (!userSession.user?.emailProviderFeatures?.email?.canViewSentMessages) {
		return (
			<div className={css(styleSheet.root)}>
				<span className={css(styleSheet.statusLine)}>
					The Email Exec Assistant needs an inbox it can read. Your current email connection is send-only, so it can’t
					be used here. Connect an account with full mailbox access (Gmail, Microsoft 365, Exchange, Yahoo, AOL, or
					IMAP) to enable it.
				</span>
			</div>
		);
	}

	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ErrorBoundary
					onReset={reset}
					fallbackRender={({ resetErrorBoundary }) => (
						<div className={css(styleSheet.root)}>
							<span className={css(styleSheet.statusLine)}>We couldn’t load your Email Exec Assistant settings.</span>
							<Button variant='outline' onClick={() => resetErrorBoundary()}>
								Try again
							</Button>
						</div>
					)}
				>
					<React.Suspense fallback={<LoadingSpinner type='small' />}>
						<EmailExecAssistantSettingsContent />
					</React.Suspense>
				</ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
};
